import React from 'react';
import Layout from '../components/layout/Layout';

const PrivacyPage: React.FC = () => {
  return (
    <Layout>
      <div className="container mx-auto px-4 py-16">
        <h1 className="text-4xl font-bold text-center mb-8 text-procreative-green dark:text-procreative-gold">Privacy Policy</h1>
        <div className="prose dark:prose-dark lg:prose-xl mx-auto">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Effective Date: June 22, 2026
          </p>
          
          <p>
            Welcome to LMC ProCreative Pty Ltd (CIPC reg number: 2022 / 698353 / 07) ("we," "us," or "our"). We are committed to protecting your privacy and ensuring that your personal information is processed in a lawful, fair, and transparent manner. This Privacy Policy details our practices regarding the collection, use, processing, and protection of personal information in compliance with the <strong>Protection of Personal Information Act, No. 4 of 2013 ("POPIA")</strong> of South Africa.
          </p>

          <h2>1. Scope of This Policy</h2>
          <p>
            This Privacy Policy applies to the ProCreative Solutions Catalogue website (accessible at <a href="https://procreative.co.za" className="text-procreative-gold hover:underline">procreative.co.za</a>) as well as the following six (6) software-as-a-service (SaaS) applications operated under our parent company, LMC ProCreative Pty Ltd (CIPC reg number: 2022 / 698353 / 07):
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>MeetingMaster</strong> - Intelligent meeting management and collaboration tool.</li>
            <li><strong>AccountsEasy</strong> - Simplified automated accounting and invoicing platform.</li>
            <li><strong>Stoccs</strong> - Real-time inventory, stock tracking, and asset management system.</li>
            <li><strong>TTriz</strong> - Systematic innovation and problem-solving software.</li>
            <li><strong>Meister Platform</strong> - Enterprise operations and business orchestration platform.</li>
            <li><strong>Social Maestro</strong> - Social media generation, management, research, and campaign coordination platform linking to Meta platforms.</li>
          </ul>
          <p>
            By accessing our website or using any of these SaaS applications, you acknowledge and agree to the practices outlined in this policy.
          </p>

          <h2>2. Personal Information We Collect</h2>
          <p>
            Under POPIA, "Personal Information" is defined as information relating to an identifiable, living, natural person, and where applicable, an identifiable, existing juristic person. We collect the following categories of information:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Identity & Contact Details:</strong> Full name, email address, phone number, physical address, company name, registration number, and job title.</li>
            <li><strong>Account Credentials:</strong> Username, hashed passwords, and security settings.</li>
            <li><strong>Billing & Financial Data:</strong> Payment details, invoicing addresses, VAT numbers, and transaction history.</li>
            <li><strong>Usage & System Logs:</strong> IP address, browser type, operating system, page views, access times, and activities performed within our SaaS products.</li>
            <li><strong>Customer Data:</strong> Data and files uploaded, entered, or generated by users when using the features of our SaaS products (e.g., meeting logs in MeetingMaster, stock records in Stoccs, etc.).</li>
          </ul>

          <h2>3. Purpose of Processing Personal Information</h2>
          <p>
            We process your personal information only for specific, explicitly defined, and lawful purposes related to our business operations, including:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Providing, maintaining, and improving our website and SaaS applications.</li>
            <li>Managing user registration, authentication, and secure access.</li>
            <li>Processing subscription payments, billing, and financial auditing.</li>
            <li>Providing customer support and troubleshooting technical difficulties.</li>
            <li>Communicating product updates, service notices, and security advisories.</li>
            <li>Ensuring system security, preventing fraudulent activity, and complying with statutory and legal obligations in South Africa.</li>
          </ul>

          <h2>4. Data Residency (Where Your Data is Hosted)</h2>
          <p>
            We adhere strictly to data sovereignty guidelines. <strong>All personal information, client databases, transactional histories, and application data processed by LMC ProCreative Pty Ltd and its 5 SaaS applications are hosted and reside within the Republic of South Africa</strong>.
          </p>
          <p>
            Our infrastructure is hosted in secure, state-of-the-art data centers located locally (utilizing local South African cloud availability zones and secure server environments). This ensures that your data never leaves South African jurisdiction without appropriate safeguards and compliance, meeting both POPIA regulations and local latency optimization goals.
          </p>

          <h2>5. Data Security</h2>
          <p>
            We implement comprehensive technical and organizational security measures to protect your personal information from unauthorized access, loss, destruction, alteration, or disclosure. These measures include:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Encryption of data in transit (using SSL/TLS protocols) and data at rest.</li>
            <li>Strict role-based access control (RBAC) to ensure only authorized personnel access databases.</li>
            <li>Regular security assessments, software updates, and vulnerability scans.</li>
            <li>Comprehensive backup systems to ensure business continuity and data recovery.</li>
          </ul>
          <p>
            While we take every reasonable precaution, no method of transmission or electronic storage is completely secure. In the event of a suspected or actual data breach, we will notify both the South African Information Regulator and affected data subjects as required by Section 22 of POPIA.
          </p>

          <h2>6. Data Retention</h2>
          <p>
            We retain personal information only for as long as is necessary to fulfill the purposes outlined in this Privacy Policy, unless:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Retention of the record is required or authorized by South African law.</li>
            <li>We require the record for lawful purposes related to our functions or activities.</li>
            <li>Retention of the record is required by a contract between the parties.</li>
            <li>The data subject has consented to the longer retention period.</li>
          </ul>

          <h2>7. Sharing of Personal Information</h2>
          <p>
            We do not sell, rent, or trade your personal information. We may share your information only under the following conditions:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Third-Party Service Providers:</strong> Trusted operators who assist us in hosting, billing, payment processing, or operational logistics under strict confidentiality and processing agreements.</li>
            <li><strong>Legal & Regulatory Mandates:</strong> Where required to comply with court orders, regulatory agencies, or South African law enforcement requests.</li>
            <li><strong>Business Transfers:</strong> In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business.</li>
          </ul>

          <h2>8. Your Rights as a Data Subject</h2>
          <p>
            Under POPIA, you possess specific rights regarding your personal information. You have the right to:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Request Access:</strong> Request confirmation and details of what personal information we hold about you.</li>
            <li><strong>Request Correction:</strong> Request that we correct, update, or complete inaccurate or outdated personal information.</li>
            <li><strong>Request Deletion:</strong> Request that we destroy, delete, or de-identify personal information that we are no longer authorized to retain.</li>
            <li><strong>Object to Processing:</strong> Object to the processing of your personal information on reasonable grounds, or object to processing for direct marketing purposes.</li>
            <li><strong>Withdraw Consent:</strong> Where processing is based on consent, you may withdraw your consent at any time.</li>
            <li><strong>Lodge a Complaint:</strong> Lodge a complaint with the South African Information Regulator if you believe we have violated your rights under POPIA.</li>
          </ul>

          <h2>9. Information Officer Contact Details</h2>
          <p>
            LMC ProCreative Pty Ltd has appointed an Information Officer responsible for ensuring compliance with POPIA. If you wish to exercise any of your rights, have questions, or require further clarification regarding our privacy practices, please contact us:
          </p>
          <ul>
            <li><strong>Information Officer Email:</strong> <a href="mailto:info@procreative.co.za" className="text-procreative-gold hover:underline">info@procreative.co.za</a></li>
            <li><strong>Contact Webpage:</strong> <a href="/contact" className="text-procreative-gold hover:underline">procreative.co.za/contact</a></li>
          </ul>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            For more details on POPIA or to lodge a complaint, you may contact the Information Regulator of South Africa at <a href="https://inforegulator.org.za" target="_blank" rel="noopener noreferrer" className="text-procreative-gold hover:underline">inforegulator.org.za</a>.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPage; 